import type { ElementDef, ElementDisplayConfig, FeatureType } from '../graphpad.model';
import { isFeatureElementDef } from '../graphpad.model';
import type { CreatedElement, ElementContext, ElementCreator, FunctionElement } from './element.model';
import { isFunctionElement } from './element.model';

/**
 * ElementResolver handles dependency resolution and element creation orchestration.
 * It ensures elements are created in the correct order based on their dependencies.
 */
export class ElementResolver {
  private readonly creators: Map<string, ElementCreator> = new Map();

  registerCreator(creator: ElementCreator): void {
    creator.types.forEach((type) => {
      this.creators.set(type, creator);
    });
  }

  registerCreators(creators: ElementCreator[]): void {
    creators.forEach((creator) => this.registerCreator(creator));
  }

  /**
   * Topologically sort elements by dependencies.
   * Returns element IDs in the order they should be created.
   */
  resolveDependencies(elements: Record<string, ElementDef>): string[] {
    const sorted: string[] = [];
    const visited = new Set<string>();
    const visiting = new Set<string>();

    const visit = (id: string) => {
      if (visited.has(id)) {
        return;
      }
      if (visiting.has(id)) {
        throw new Error(`Circular dependency detected: ${id}`);
      }

      visiting.add(id);
      const def = elements[id];

      if (!def) {
        throw new Error(`Element '${id}' not found in elements`);
      }

      // Check for dependencies - all elements with dependencies have `of` in params
      if ('params' in def && def.params && 'of' in def.params) {
        const ofValue = def.params['of'];
        if (Array.isArray(ofValue)) {
          // Multi-element feature (e.g., intersection) has multiple dependencies
          (ofValue as string[]).forEach((ref) => visit(ref));
        } else {
          // Single-element feature or handle has one dependency
          visit(ofValue as string);
        }
      }

      visiting.delete(id);
      visited.add(id);
      sorted.push(id);
    };

    Object.keys(elements).forEach(visit);
    return sorted;
  }

  /**
   * Create a single element using the appropriate creator.
   * For features, validates that the parent function supports this feature type.
   */
  createElement(
    id: string,
    def: ElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const creator = this.creators.get(def.type);
    if (!creator) {
      throw new Error(
        `Unknown element type: ${def.type}. Available types: ${Array.from(this.creators.keys()).join(', ')}`,
      );
    }

    // Validate feature against parent's availableFeatures
    if (isFeatureElementDef(def)) {
      this.validateFeature(id, def.type as FeatureType, def.params, context);
    }

    return creator.create(id, def, display, context);
  }

  getRegisteredTypes(): string[] {
    return Array.from(this.creators.keys());
  }

  /**
   * Validate that a feature is supported by its parent function(s).
   * @throws Error if the feature is not available for the parent function type
   */
  private validateFeature(
    featureId: string,
    featureType: FeatureType,
    params: Record<string, unknown> | undefined,
    context: ElementContext,
  ): void {
    // Skip validation for features that don't have a parent reference
    if (!params || !('of' in params)) {
      return;
    }

    const parentRefs = params['of'];
    const parentsToCheck = Array.isArray(parentRefs) ? parentRefs : [parentRefs];

    for (const parentId of parentsToCheck) {
      if (typeof parentId !== 'string') {
        continue;
      }

      const parentElement = context.elements.get(parentId);
      if (!parentElement || !isFunctionElement(parentElement)) {
        continue;
      }

      const parent = parentElement as FunctionElement;
      const availableFeatures = parent.availableFeatures;

      if (!availableFeatures.includes(featureType)) {
        throw new Error(
          `Feature '${featureType}' is not available for function type '${parent.type}'. ` +
            `Element '${featureId}' references '${parentId}'. ` +
            `Available features: ${availableFeatures.join(', ')}`,
        );
      }
    }
  }
}
